import { useMemo } from 'react'
import { Badge, Button, Group, Tooltip } from '@mantine/core'
import { MantineReactTable, useMantineReactTable, type MRT_ColumnDef } from 'mantine-react-table'
import { PaginationState, ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { tableDefaultProps, tableDefaultAlertBannerProps } from '@shared/components/table/table'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { IconEdit } from '@tabler/icons-react'
import {
  ListOffersResponse,
  LoomWoolLotOfferStatus,
  OfferDto,
  OfferStatus,
} from '@shared/grower-api/.generated/types'
import {
  OfferStatusDisplay,
  OfferStatusOptionsLabels,
} from '@shared/components/offers/offer-status-display/offer-status-display'
import { useNavigation } from '@/hooks/use-navigation'

type OffersTableProps = {
  data: ListOffersResponse | undefined
  stationRecordNumber: string
  isError: boolean
  isFetching: boolean
  isLoading: boolean
  pagination: PaginationState
  setPagination: OnChangeFn<PaginationState>
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}

export function OffersTable({
  data,
  stationRecordNumber,
  isError,
  isFetching,
  isLoading,
  pagination,
  setPagination,
  columnFilters,
  setColumnFilters,
}: OffersTableProps) {
  const { navigateToViewOffer } = useNavigation()

  const columns = useMemo<MRT_ColumnDef<OfferDto>[]>(
    () => [
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableColumnFilter: true,
        filterVariant: 'multi-select',
        Cell: ({ cell, row }) => (
          <Group gap='sm'>
            <OfferStatusDisplay
              loomWoolLotOfferStatus={cell.getValue<LoomWoolLotOfferStatus | undefined>()}
            />
            {row.original.submittedOnBehalfOfGrower && (
              <Tooltip label='This offer was responded to by Zentera on behalf of the grower'>
                <Badge color='blue.2' c='black' fw={600}>
                  On behalf
                </Badge>
              </Tooltip>
            )}
          </Group>
        ),
        mantineFilterMultiSelectProps: {
          data: [
            { value: OfferStatus.Complete, label: OfferStatusOptionsLabels.Complete },
            { value: OfferStatus.Expired, label: OfferStatusOptionsLabels.Expired },
            { value: OfferStatus.UnderOffer, label: OfferStatusOptionsLabels.UnderOffer },
          ],
        },
      },
      {
        id: 'offerFrom',
        accessorKey: 'offerFrom',
        header: 'From',
        enableColumnFilter: false,
      },
      {
        id: 'created',
        accessorKey: 'created',
        header: 'Sent',
        enableColumnFilter: false,
        accessorFn: (row) => (row.created == null ? null : new Date(row.created)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
      {
        id: 'paymentDate',
        accessorKey: 'paymentDate',
        header: 'Payment',
        enableColumnFilter: false,
        accessorFn: (row) => (row.paymentDate == null ? null : new Date(row.paymentDate)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY'),
      },
      {
        id: 'expiryDateTime',
        accessorKey: 'expiryDateTime',
        header: 'Expiry',
        enableColumnFilter: false,
        accessorFn: (row) => (row.expiryDateTime == null ? null : new Date(row.expiryDateTime)),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
      {
        id: 'completedDateTime',
        accessorKey: 'completedDateTime',
        header: 'Submitted',
        enableColumnFilter: false,
        accessorFn: (row) =>
          row.completedDateTime == null ? null : new Date(row.completedDateTime),
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YYYY h:mm A'),
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data: data?.results ?? [],
    initialState: {
      ...tableDefaultProps.initialState,
      showColumnFilters: true,
    },
    state: {
      isLoading,
      columnFilters,
      pagination,
      showProgressBars: isFetching,
      showAlertBanner: isError,
    },
    mantineToolbarAlertBannerProps: tableDefaultAlertBannerProps(isError),
    mantineTableBodyRowProps: ({ row }) => ({
      'data-testid': `offer-row-${row.original.id}`,
      ref: undefined, // Ensure compatibility with the expected type
    }),
    // Pagination
    manualPagination: true,
    rowCount: data?.totalResultCount,
    pageCount: data?.totalPageCount,
    onPaginationChange: setPagination,
    // Column filters
    enableFilters: true,
    manualFiltering: true,
    onColumnFiltersChange: setColumnFilters,
    // Actions
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <>
        {row.original.status === OfferStatus.UnderOffer && (
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              if (row.original.id) {
                navigateToViewOffer(stationRecordNumber, row.original.id)
              }
            }}
            leftSection={<IconEdit />}
          >
            Respond to offer
          </Button>
        )}
        {(row.original.status === OfferStatus.Complete ||
          row.original.status === OfferStatus.Expired) && (
          <Button
            size='xs'
            variant='outline'
            onClick={() => {
              if (row.original.id) {
                navigateToViewOffer(stationRecordNumber, row.original.id)
              }
            }}
          >
            View offer
          </Button>
        )}
      </>
    ),
  })

  return <MantineReactTable table={table} />
}
